import React from 'react'
import { WemoloTypography } from '../Wemolo'
import styles from './Table.module.css'

export interface CustomTableProps {
    columns: ColumnType[]
    data: DataRowType[]
}

// Columns that don't declare a width fall back to this, so nothing shrinks to unreadable.
const DEFAULT_COLUMN_WIDTH = '150px'

const getColumnWidth = (column: ColumnType): string => column.width ?? DEFAULT_COLUMN_WIDTH

/**
 * A plain table rather than a flex one: `table-layout: fixed` plus a `<colgroup>` makes the browser
 * hold every column to its declared width and keeps the header aligned with its rows for free.
 * The min-width stops the columns being squeezed when they no longer fit — the wrapper scrolls
 * instead, header and rows together.
 */
export const Table: React.FC<CustomTableProps> = ({ columns, data }) => (
    <div className={styles.scrollContainer}>
        <table className={styles.table} style={{ minWidth: `calc(${columns.map(getColumnWidth).join(' + ')})` }}>
            <colgroup>
                {columns.map((column) => (
                    <col key={column.key} style={{ width: getColumnWidth(column) }} />
                ))}
            </colgroup>
            <thead>
                <tr>
                    {columns.map((column) => (
                        <th key={column.key} className={styles.headCell}>
                            <WemoloTypography variant="title-small" tag="span">
                                {column.label}
                            </WemoloTypography>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.length === 0 ? (
                    <tr>
                        <td className={styles.emptyCell} colSpan={columns.length}>
                            No Data
                        </td>
                    </tr>
                ) : (
                    data.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {columns.map((column) => (
                                <td key={column.key} className={styles.cell}>
                                    {row[column.key]}
                                </td>
                            ))}
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    </div>
)

export interface ColumnType {
    key: string
    label: string
    width?: string
}

export interface DataRowType {
    [key: string]: string | number | boolean | undefined | React.ReactNode
}
